import { InMemoryCache, Reference } from '@apollo/client';
import { ReadFieldFunction } from '@apollo/client/cache/core/types/common';
import { ILocationFilterProps } from 'components/LocationFilterInput/LocationFilterInput.types';
import {
  PositionByDepartmentResponseDataType,
  PositionByDepartmentSelectOptionType,
} from 'components/PositionByDepartmentSelectInput/PositionsByDepartmentSelectInput.types';
import { useSSFBehaviors } from 'hooks';
import { createApolloClient } from 'providers/ApolloProvider';
import { QUERY_positionByDepartmentSelectInput } from 'queries';
import { createContext, FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isUserB2bSelector } from 'store/selectors/userSelectors/isUserB2bSelector';
import isUserSecondarySelector from 'store/selectors/userSelectors/isUserSecondarySelector';
import { useUserTypeSelector } from 'store/selectors/userSelectors/userTypeSelector';
import { GQLAPIContextType, pageContextInitialState } from 'types/common.types';
import { GroupedPositionsConnection, PositionDetails } from 'types/graphql';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import isUserEssentialSelector from 'store/selectors/userSelectors/isUserEssentialSelector';

export const PositionByDepartmentSelectInputContext = createContext(
  pageContextInitialState,
) as GQLAPIContextType;
const capitalize = (s: string) => (s && s[0].toUpperCase() + s.slice(1)) || '';
const snakeToTitleCase = (s: string) => s.split('_').map(capitalize).join(' ');

export type GroupedPositions = {
  group: string;

  positions?: PositionDetails[];
};

export const PositionByDepartmentSelectInputProvider: FC<{
  location?: ILocationFilterProps['value'];
  locationIds?: string[];
  skipFetchOnMount?: boolean;
}> = ({ location, locationIds: locationIdsProp, skipFetchOnMount, children }) => {
  const userType = useUserTypeSelector();
  const isUserB2B = useSelector(isUserB2bSelector);
  const isUserEssential = useSelector(isUserEssentialSelector);
  const isUserSecondary = useSelector(isUserSecondarySelector);
  const locationId = location?.value || getCurrentlySelectedLocation()?.value;

  const client = useMemo(
    () =>
      createApolloClient({
        cache: new InMemoryCache({
          typePolicies: {
            Query: {
              fields: {
                groupPositions: {
                  keyArgs: false,
                  merge(
                    existing = { edges: [] },
                    incoming = { edges: [] },
                    { readField }: { readField: ReadFieldFunction },
                  ) {
                    const nextEdges: { node: Reference }[] = [...existing.edges];
                    if (incoming.edges?.length > 0)
                      incoming.edges.forEach(({ node }: { node: Reference }) => {
                        const groupId = readField('group', node);
                        if (
                          groupId &&
                          !nextEdges.some(({ node: _node }) => {
                            const _groupId = readField('group', _node);
                            return _groupId === groupId;
                          })
                        )
                          nextEdges.push({ node });
                      });

                    return { ...existing, ...incoming, edges: nextEdges };
                  },
                },
              },
            },
          },
        }),
      }),
    [],
  );

  const [values, useQueryOptions] = useSSFBehaviors({
    key: 'POSITION_BY_FACILITY_FILTER',
    query: QUERY_positionByDepartmentSelectInput,
    pageSize: 30,
    paginationType: 'InfiniteScroll',
    client,
    payloadTransformer: (variables) => {
      const communityIds = locationIdsProp?.length
        ? locationIdsProp.map(Number)
        : locationId
        ? [Number(locationId)]
        : undefined;

      const payload = {
        ...variables,
        filter: {
          byName: variables.byName,
          byAgencyCommunity: undefined,
          ...((isUserB2B || isUserSecondary) &&
            !isUserEssential &&
            communityIds && {
              allocatedToCommunities: communityIds,
            }),
        },
      };
      if (userType === 'Agency' && communityIds?.length === 1) {
        payload.filter.byAgencyCommunity = communityIds[0];
      }
      return payload;
    },
    responseTransformer: (data): PositionByDepartmentResponseDataType => {
      if (!data) return { options: {} };
      if (!data.groupPositions) return { options: {} };

      const { edges, pageInfo } = data?.groupPositions as GroupedPositionsConnection;
      const filteredEdges = edges?.filter((edge) => !!edge?.node) as {
        node: GroupedPositions;
      }[];

      let options: Record<string, PositionByDepartmentSelectOptionType[]> = {};
      filteredEdges.forEach(({ node: { positions, group } }) => {
        if (positions) {
          options[snakeToTitleCase(group)] = positions.map((position) => {
            return {
              value: position.displayId,
              label: position.name,
              key: position.displayId.toString(),
            };
          });
        }
      });
      return { options, pageInfo };
    },
    skipFetchOnMount,
    schema: {
      byFullName: {
        initialValue: '',
        mapper: String,
      },
    },
  });

  const onClose = useCallback(() => {
    if (!values.byName) return;
    useQueryOptions.onChange({
      target: { name: 'byName', value: '' },
    });
  }, [useQueryOptions, values]);

  const value = useMemo(() => {
    return { onClose, values, ...useQueryOptions };
  }, [onClose, values, useQueryOptions]);

  return (
    <PositionByDepartmentSelectInputContext.Provider value={value}>
      {children}
    </PositionByDepartmentSelectInputContext.Provider>
  );
};
